<template>
  <div class="telegram-binding">
    <div v-if="loading" class="telegram-binding__loading">
      <UiProgress variant="spinner" indeterminate />
      <span>Загрузка...</span>
    </div>

    <div v-else-if="status?.isBound" class="telegram-binding__bound">
      <div class="telegram-binding__icon">✅</div>
      <div class="telegram-binding__info">
        <p class="telegram-binding__title">Telegram привязан</p>
        <p v-if="status.telegramUsername" class="telegram-binding__username">
          @{{ status.telegramUsername }}
        </p>
      </div>
      <UiButton variant="secondary" size="small" @click="handleUnbind">
        Отвязать
      </UiButton>
    </div>

    <div v-else-if="bindCode" class="telegram-binding__code">
      <p class="telegram-binding__title">Привязка Telegram</p>
      <ol class="telegram-binding__steps">
        <li>Откройте бота <a :href="botLink ?? undefined" target="_blank" rel="noopener">@LabActiveBot</a></li>
        <li>Отправьте команду:</li>
      </ol>
      <div class="telegram-binding__code-box">
        <code>/start {{ bindCode }}</code>
        <UiButton variant="ghost" size="small" @click="copyCode">
          📋 Копировать
        </UiButton>
      </div>
      <p class="telegram-binding__hint">Код действителен 10 минут</p>
      <UiButton variant="secondary" size="small" @click="bindCode = null">
        Отмена
      </UiButton>
    </div>

    <div v-else class="telegram-binding__action">
      <div class="telegram-binding__icon">📱</div>
      <div class="telegram-binding__info">
        <p class="telegram-binding__title">Telegram</p>
        <p class="telegram-binding__subtitle">
          Получайте уведомления о заказах прямо в мессенджер
        </p>
      </div>
      <UiButton size="small" @click="handleGenerateCode">
        Привязать
      </UiButton>
    </div>

    <p v-if="error" class="telegram-binding__error">{{ error }}</p>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'
import { useTelegramBinding } from '@/modules/common/composables/useTelegramBinding'
import UiButton from '@/shared/ui/UiButton.vue'
import UiProgress from '@/shared/ui/UiProgress.vue'

const { loading, error, status, available, bindCode, botLink, fetchStatus, generateBindCode, unbind } = useTelegramBinding()

const emit = defineEmits<{
  availability: [available: boolean]
}>()

onMounted(async () => {
  await fetchStatus()
  emit('availability', available.value !== false)
})

async function handleGenerateCode() {
  await generateBindCode()
}

async function handleUnbind() {
  if (confirm('Отвязать Telegram? Уведомления перестанут приходить.')) {
    await unbind()
  }
}

function copyCode() {
  if (bindCode.value) {
    navigator.clipboard.writeText(`/start ${bindCode.value}`)
  }
}
</script>

<style scoped>
.telegram-binding {
  padding: 16px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: #f8fafc;
}

.telegram-binding__loading,
.telegram-binding__bound,
.telegram-binding__action {
  display: flex;
  align-items: center;
  gap: 12px;
}

.telegram-binding__icon {
  font-size: 24px;
  flex-shrink: 0;
}

.telegram-binding__info {
  flex: 1;
  min-width: 0;
}

.telegram-binding__title {
  margin: 0;
  font-weight: 600;
  font-size: 14px;
}

.telegram-binding__subtitle {
  margin: 4px 0 0;
  font-size: 13px;
  color: #586578;
}

.telegram-binding__username {
  margin: 4px 0 0;
  font-size: 13px;
  color: var(--color-accent);
}

.telegram-binding__code {
  text-align: center;
}

.telegram-binding__steps {
  text-align: left;
  font-size: 13px;
  color: #475569;
  margin: 12px 0;
  padding-left: 20px;
}

.telegram-binding__steps a {
  color: var(--color-accent);
  text-decoration: none;
}

.telegram-binding__code-box {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: #0f172a;
  color: #22c55e;
  padding: 12px 16px;
  border-radius: 8px;
  font-family: monospace;
  font-size: 14px;
  margin: 12px 0;
}

.telegram-binding__hint {
  font-size: 12px;
  color: #94a3b8;
  margin: 8px 0 16px;
}

.telegram-binding__error {
  margin: 8px 0 0;
  font-size: 13px;
  color: #dc2626;
}
</style>
